import { isRecord } from './guards.ts'

export const PROTOCOL_VERSION = 1

export const ERROR_CODES = [
  'session_expired',
  'origin_rejected',
  'action_forbidden',
  'customer_not_found',
  'customer_ambiguous',
  'duplicate_card',
  'validation',
  'processor_decline',
  'attempt_limit',
  'payment_unknown',
  'rate_limited',
  'version_unsupported',
  'unavailable'
] as const

export type ErrorCode = (typeof ERROR_CODES)[number]

export type TerminalStatus = 'loading' | 'awaiting_input' | 'submitting'

export interface Envelope<P = Record<string, unknown>> {
  type: string
  version: number
  generation: number
  id: string
  payload: P
}

export type ParseResult =
  | { kind: 'ok'; envelope: Envelope }
  | { kind: 'unsupported_version'; id: string }
  | { kind: 'ignore' }

const MAX_ID_LENGTH = 128
const MAX_SECRET_LENGTH = 1024

let counter = 0

function nextId(): string {
  counter += 1
  return `${Date.now().toString(36)}-${counter.toString(36)}`
}

/**
 * Wraps a payload in a version 1 envelope.
 * @param type - The message type.
 * @param generation - The session generation the message belongs to.
 * @param payload - The message payload.
 * @returns The envelope, ready for postMessage.
 */
export function makeEnvelope<P>(
  type: string,
  generation: number,
  payload: P
): Envelope<P> {
  return { type, version: PROTOCOL_VERSION, generation, id: nextId(), payload }
}

/**
 * Validates untrusted postMessage data as an envelope.
 * @param data - The `MessageEvent.data` value.
 * @returns The envelope, an unsupported-version marker, or `ignore`.
 */
export function parseEnvelope(data: unknown): ParseResult {
  if (!isRecord(data)) return { kind: 'ignore' }
  const { type, version, generation, id } = data
  const payload = data.payload ?? {}
  if (typeof type !== 'string' || type.length === 0) return { kind: 'ignore' }
  if (typeof id !== 'string' || id.length === 0 || id.length > MAX_ID_LENGTH) {
    return { kind: 'ignore' }
  }
  if (typeof version !== 'number' || !Number.isInteger(version)) {
    return { kind: 'ignore' }
  }
  if (
    typeof generation !== 'number' ||
    !Number.isInteger(generation) ||
    generation < 0
  ) {
    return { kind: 'ignore' }
  }
  if (!isRecord(payload)) return { kind: 'ignore' }
  if (version !== PROTOCOL_VERSION) return { kind: 'unsupported_version', id }
  return { kind: 'ok', envelope: { type, version, generation, id, payload } }
}

const ERROR_CODE_SET: ReadonlySet<string> = new Set(ERROR_CODES)

/**
 * Checks that a value is one of the protocol error codes.
 * @param value - An untrusted value.
 * @returns True when the value is an ErrorCode.
 */
export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && ERROR_CODE_SET.has(value)
}

/**
 * Reads the client secret from an `init` payload.
 * @param payload - The envelope payload.
 * @returns The secret, or null when the payload is malformed.
 */
export function parseInitPayload(
  payload: Record<string, unknown>
): string | null {
  const secret = payload.clientSecret
  if (typeof secret !== 'string') return null
  if (secret.length === 0 || secret.length > MAX_SECRET_LENGTH) return null
  return secret
}
